import React, { useState } from 'react';
import StatCard from '../components/StatCard';
import SeverityBadge from '../components/SeverityBadge';
import DataTable from '../components/DataTable';
import CategoryDetailModal from '../components/CategoryDetailModal';
import useAPI from '../hooks/useAPI';
import { getErrorStats, getProjects } from '../services/api';

export default function ErrorStats() {
  const [days, setDays] = useState(30);
  const [projectId, setProjectId] = useState('');
  const [country, setCountry] = useState('');
  const { data: projectsData } = useAPI(getProjects);
  const { data, loading } = useAPI(
    () => getErrorStats(projectId || undefined, days, country),
    [projectId, days, country]
  );

  const [selectedCategory, setSelectedCategory] = useState(null);
  const projects = projectsData?.projects || [];

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading error statistics...</div>;

  const errorCodes = data?.error_codes || [];
  const byCategory = data?.by_category || [];
  const bySeverity = data?.by_severity || {};
  const comparison = data?.country_comparison || {};
  const totalRobots = data?.total_robots || 1;

  const COUNTRY_OPTIONS = [
    { key: '', label: 'All' },
    { key: 'Israel', label: 'Israel' },
    { key: 'India', label: 'India' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Error Statistics</h1>
          <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
            {data?.total_errors?.toLocaleString() || 0} errors across {data?.unique_codes || 0} codes
            {totalRobots > 1 && <span> · {totalRobots.toLocaleString()} robots · {(data?.total_errors / totalRobots).toFixed(1)} errors/robot</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Region filter — button group */}
          <div style={styles.countryGroup}>
            {COUNTRY_OPTIONS.map((opt, i, arr) => {
              const active = country === opt.key;
              return (
                <button
                  key={opt.key || 'all'}
                  onClick={() => { setCountry(opt.key); setProjectId(''); }}
                  style={{
                    ...styles.countryBtn,
                    ...(active ? styles.countryBtnActive : {}),
                    borderTopLeftRadius: i === 0 ? 6 : 0,
                    borderBottomLeftRadius: i === 0 ? 6 : 0,
                    borderTopRightRadius: i === arr.length - 1 ? 6 : 0,
                    borderBottomRightRadius: i === arr.length - 1 ? 6 : 0,
                    borderLeft: i === 0 ? '1px solid #dfe6e9' : 'none',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} style={styles.select}>
            <option value="">All Projects</option>
            {projects
              .filter(p => !country
                || (country === 'Israel' && (p.country === 'IL' || p.country === 'Israel'))
                || (country === 'India' && (p.country === 'IN' || p.country === 'India')))
              .map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
          </select>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={styles.select}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Severity Summary */}
      <div style={styles.cardGrid}>
        <StatCard label="Critical (RED)" value={(bySeverity.RED || 0).toLocaleString()} color="#d63031" />
        <StatCard label="Warning (YELLOW)" value={(bySeverity.YELLOW || 0).toLocaleString()} color="#e17055" />
        <StatCard label="Info (GREEN)" value={(bySeverity.GREEN || 0).toLocaleString()} color="#00b894" />
        <StatCard label="Total Errors" value={data?.total_errors?.toLocaleString() || 0} color="#6c5ce7" />
      </div>

      <div style={styles.twoCol}>
        {/* Error codes table */}
        <div>
          <h2 style={styles.sectionTitle}>Error Codes</h2>
          <DataTable
            columns={[
              { key: 'code', label: 'Code' },
              { key: 'count', label: 'Count', align: 'right', render: v => v?.toLocaleString() },
              { key: 'per_robot', label: 'Per Robot', align: 'right',
                render: v => <span style={{ fontWeight: 600, color: v >= 5 ? '#d63031' : v >= 2 ? '#e17055' : '#636e72' }}>{v?.toFixed(1)}</span>
              },
              { key: 'robots_affected', label: 'Robots', align: 'right', render: v => v?.toLocaleString() },
              { key: 'severity', label: 'Severity', render: v => <SeverityBadge severity={v} /> },
              {
                key: 'pct',
                label: '% of Total',
                align: 'right',
                render: (v, row) => {
                  const total = data?.total_errors || 1;
                  return `${((row.count / total) * 100).toFixed(1)}%`;
                },
              },
            ]}
            data={errorCodes.slice(0, 25)}
          />
        </div>

        {/* Right column */}
        <div>
          {/* Country Comparison Chart */}
          {comparison.Israel && comparison.India && (
            <>
              <h2 style={styles.sectionTitle}>Errors per Robot — Israel vs India</h2>
              <CountryComparisonChart comparison={comparison} />
            </>
          )}

          {/* By category */}
          <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>By Category</h2>
          {byCategory.length > 0 ? (
            <div>
              {byCategory.map(cat => {
                const maxCount = byCategory[0]?.count || 1;
                return (
                  <div
                    key={cat.category}
                    style={{ ...styles.catRow, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onClick={() => setSelectedCategory(cat.category)}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(108,92,231,0.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)'; }}
                    title="Click for deep analysis"
                  >
                    <div style={styles.catLabel}>
                      <span style={{ fontWeight: 600 }}>{cat.category} <span style={{ fontSize: 11, color: '#6c5ce7' }}>&#x1F50D;</span></span>
                      <span style={{ color: '#636e72', fontSize: 12 }}>
                        {cat.count?.toLocaleString()} ({cat.codes || 0} codes)
                      </span>
                    </div>
                    <div style={styles.catBarBg}>
                      <div
                        style={{
                          ...styles.catBarFill,
                          width: `${(cat.count / maxCount) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#999', padding: 20, textAlign: 'center', background: '#fff', borderRadius: 10 }}>
              No category data available
            </div>
          )}

          {/* Severity distribution */}
          <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>Severity Distribution</h2>
          <div style={styles.severityChart}>
            {Object.entries(bySeverity).map(([level, count]) => {
              const total = data?.total_errors || 1;
              const pct = ((count / total) * 100).toFixed(1);
              const colors = { RED: '#d63031', YELLOW: '#e17055', GREEN: '#00b894' };
              return (
                <div key={level} style={styles.severityRow}>
                  <div style={{ width: 60, fontWeight: 600, fontSize: 12, color: colors[level] || '#636e72' }}>
                    {level}
                  </div>
                  <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: colors[level] || '#636e72',
                      borderRadius: 4,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <div style={{ width: 80, textAlign: 'right', fontSize: 12, color: '#636e72' }}>
                    {count?.toLocaleString()} ({pct}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category Detail Modal */}
      {selectedCategory && (
        <CategoryDetailModal
          category={selectedCategory}
          days={days}
          onClose={() => setSelectedCategory(null)}
        />
      )}
    </div>
  );
}

/** Country comparison bar chart */
function CountryComparisonChart({ comparison }) {
  const israel = comparison.Israel;
  const india = comparison.India;

  // Merge all categories
  const allCats = new Set();
  israel.by_category.forEach(c => allCats.add(c.category));
  india.by_category.forEach(c => allCats.add(c.category));

  const israelMap = {};
  israel.by_category.forEach(c => { israelMap[c.category] = c.per_robot; });
  const indiaMap = {};
  india.by_category.forEach(c => { indiaMap[c.category] = c.per_robot; });

  // Sort by total per-robot (sum of both)
  const categories = [...allCats].sort((a, b) =>
    ((israelMap[b] || 0) + (indiaMap[b] || 0)) - ((israelMap[a] || 0) + (indiaMap[a] || 0))
  );

  const maxVal = Math.max(
    ...categories.map(c => Math.max(israelMap[c] || 0, indiaMap[c] || 0)),
    israel.errors_per_robot,
    india.errors_per_robot
  );

  return (
    <div style={styles.comparisonChart}>
      {/* Total summary */}
      <div style={styles.compSummary}>
        <div style={styles.compCountry}>
          <span style={{ fontSize: 18 }}>🇮🇱</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Israel</div>
            <div style={{ fontSize: 11, color: '#636e72' }}>{israel.total_robots.toLocaleString()} robots</div>
          </div>
          <div style={styles.compRate}>{israel.errors_per_robot}</div>
          <div style={{ fontSize: 11, color: '#636e72' }}>errors/robot</div>
        </div>
        <div style={styles.compVs}>vs</div>
        <div style={styles.compCountry}>
          <span style={{ fontSize: 18 }}>🇮🇳</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>India</div>
            <div style={{ fontSize: 11, color: '#636e72' }}>{india.total_robots.toLocaleString()} robots</div>
          </div>
          <div style={styles.compRate}>{india.errors_per_robot}</div>
          <div style={{ fontSize: 11, color: '#636e72' }}>errors/robot</div>
        </div>
      </div>

      {/* Per-category bars */}
      <div style={{ marginTop: 16 }}>
        {categories.map(cat => (
          <div key={cat} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#2d3436', marginBottom: 4 }}>{cat}</div>
            {/* Israel bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ width: 20, fontSize: 10, textAlign: 'center' }}>🇮🇱</span>
              <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${((israelMap[cat] || 0) / maxVal) * 100}%`,
                  background: '#3498db',
                  borderRadius: 3,
                  minWidth: (israelMap[cat] || 0) > 0 ? 2 : 0,
                }} />
              </div>
              <span style={{ width: 40, fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{(israelMap[cat] || 0).toFixed(1)}</span>
            </div>
            {/* India bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 20, fontSize: 10, textAlign: 'center' }}>🇮🇳</span>
              <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${((indiaMap[cat] || 0) / maxVal) * 100}%`,
                  background: '#e67e22',
                  borderRadius: 3,
                  minWidth: (indiaMap[cat] || 0) > 0 ? 2 : 0,
                }} />
              </div>
              <span style={{ width: 40, fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{(indiaMap[cat] || 0).toFixed(1)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  select: {
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 13,
    background: '#fff',
  },
  countryGroup: {
    display: 'inline-flex',
  },
  countryBtn: {
    padding: '6px 14px',
    border: '1px solid #dfe6e9',
    borderLeft: 'none',
    background: '#fff',
    color: '#636e72',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  countryBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  catRow: {
    padding: '10px 14px',
    background: '#fff',
    borderRadius: 8,
    marginBottom: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  catLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 13,
    marginBottom: 6,
  },
  catBarBg: {
    height: 6,
    background: '#f0f0f0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  catBarFill: {
    height: '100%',
    background: '#6c5ce7',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  severityChart: {
    background: '#fff',
    borderRadius: 10,
    padding: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  severityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
  },
  comparisonChart: {
    background: '#fff',
    borderRadius: 10,
    padding: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  compSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: '8px 0 12px',
    borderBottom: '1px solid #f0f0f0',
  },
  compCountry: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center',
  },
  compRate: {
    fontSize: 22,
    fontWeight: 700,
    color: '#6c5ce7',
  },
  compVs: {
    fontSize: 14,
    fontWeight: 600,
    color: '#b2bec3',
  },
};
